import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useCallback, useMemo, useState } from 'react';
import { useIsPristine } from '../../../hooks/useIsPristine';
import { uniqueId } from '../../../lib/uniqueId';
import { CreateEditDialog } from '../../common/create-edit-dialog/CreateEditDialog';
import { Button, colors, Typography } from '@sweep-io/sweep-design';
import useCheckPermissionsForCurrentCrmOrg from '../../common/permissions/useCurrentCrmOrgPermissions';
import { CustomHeader } from '../../common/rule-builder/CustomHeader';
import { SweepFieldsRuleBuilder } from '../../common/sweep-condition-rule-builder/SweepConditionRuleBuilder';
import ErrorIcon from '@mui/icons-material/Error';
import {
  MultipleRuleBuildersProvider,
  useRefForMultipleRuleBuilders,
  useValidateAllAndReturnIsValid,
} from '../../common/sweep-condition-rule-builder/MultipleRuleBuildersContext';
import cloneDeep from 'lodash/cloneDeep';
import useConfirm from '../../common/dialogs/ConfirmLeaveWithoutSave/useConfirm';
import { ConfirmLeaveWithoutSaveDialog } from '../../common/dialogs/ConfirmLeaveWithoutSave/ConfirmLeaveWithoutSaveDialog';
import { isEmptySweepCriterion } from '../../../models/AutomationsConditionalActionModelDeprecated';
import { SweepFieldOperator } from '../../SweepFieldsOperatorSelector/sweepFieldsTypesAndOperators';
import { SfdcDisplayType } from '../../../types/fields';
import {
  greatestSupportedTypes,
  latestSupportedTypes,
  minMaxSupportedTypes,
  prioritizeSupportedTypes,
} from './helpers';
import { Permission } from '@server/permissions';
import { SweepCondition } from '../../../types/SweepCondition';

interface TieBreakerDialogProps {
  initialRule?: TieBreaker;
  onCreateEdit: (rule: TieBreaker) => void;
  crmOrgId: string;
  objectType: string;
  onCancel: () => void;
}

const SectionBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

interface FormType {
  _id: string;
  name: string;
  when: SweepCondition;
}

const listOfOperatorsToRemove = [
  SweepFieldOperator.IS_CHANGED_FALSE,
  SweepFieldOperator.IS_CHANGED_TRUE,
  SweepFieldOperator.HAS_INCREASED,
  SweepFieldOperator.HAS_DECREASED,
];

const MULTIPLE_CRITERIA_MAX_MIN_ERROR =
  'Tie breaker rules with "is latest", "is earliest", "is greatest", or "is lowest" operators cannot have multiple conditions';

const ruleHasOnlyOneCriteriaAndItIsEmpty = (rule: FormType) => {
  if (rule.when?.criteria) {
    if (rule.when.criteria.length === 1 && isEmptySweepCriterion(rule.when.criteria[0])) {
      return true;
    }
  }
  return false;
};

const sanitizeRule = (rule: FormType): TieBreaker => {
  const cloned = cloneDeep(rule);
  return cloned as TieBreaker;
};

const TieBreakerDialog = ({
  initialRule,
  onCreateEdit,
  crmOrgId,
  objectType,
  onCancel,
}: TieBreakerDialogProps) => {
  const permissionString: Permission[] = ['edit:dedupe-matching'];
  const [isAllowedBtn] = useCheckPermissionsForCurrentCrmOrg(permissionString);

  const generateNameFromCriteria = useCallback((): string => {
    // All tie breakers use the same name based on the main entity (objectType)
    return 'Tie breaker for ' + objectType;
  }, [objectType]);

  const formVarsFromInitialRule = (): FormType => {
    const { criteria = [], criteriaLogic = '' } = initialRule?.when || {};
    const when: SweepCondition = {
      criteria,
      criteriaLogic,
    };
    const name = initialRule?.name || generateNameFromCriteria();

    return {
      _id: initialRule?._id || uniqueId(),
      name,
      when,
    };
  };

  const [formVars, setFormVars] = useState<FormType>(formVarsFromInitialRule());

  const isPristine = useIsPristine(formVars);
  const { openConfirm } = useConfirm();

  const hasMultipleCriteriaWithMaxMin = useMemo(() => {
    const criteria = formVars.when?.criteria || [];
    if (criteria.length < 2) {
      return false;
    }

    // Check if at least one criterion uses MAX or MIN operator
    return criteria.some(
      (c) => c.operator === SweepFieldOperator.MAX || c.operator === SweepFieldOperator.MIN,
    );
  }, [formVars.when?.criteria]);

  const isFormValid = useMemo(() => {
    const isNameValid = formVars.name.length > 0;
    const hasCriteria = formVars.when?.criteria && formVars.when.criteria.length > 0;
    const isNotEmpty = !ruleHasOnlyOneCriteriaAndItIsEmpty(formVars);

    return isNameValid && hasCriteria && isNotEmpty && !hasMultipleCriteriaWithMaxMin;
  }, [formVars, hasMultipleCriteriaWithMaxMin]);

  const ruleBuilderRef = useRefForMultipleRuleBuilders();
  const validateAllAndReturnIsValid = useValidateAllAndReturnIsValid();

  const applyCurrentRule = useCallback(() => {
    const isValid = validateAllAndReturnIsValid() && !ruleHasOnlyOneCriteriaAndItIsEmpty(formVars);

    if (isValid) {
      const sanitizedRule = sanitizeRule(formVars);
      onCreateEdit(sanitizedRule);
    }
  }, [formVars, validateAllAndReturnIsValid, onCreateEdit]);

  const closeOrConfirmClose = async () => {
    if (!isPristine) {
      const isConfirmed = await openConfirm(<ConfirmLeaveWithoutSaveDialog />);
      if (!isConfirmed) {
        return;
      }
    }
    onCancel();
  };

  const _onCriteriaChange = useCallback(
    async (newSweepCondition: SweepCondition) => {
      // Name doesn't change based on criteria - it's always the same for all tie breakers
      setFormVars({
        ...formVars,
        when: newSweepCondition,
      });
    },
    [formVars],
  );

  const filterOperatorBy = (operatorList: OperatorWithIndex[], sfDisplayType?: SfdcDisplayType) => {
    let operatorsToRemove = [...listOfOperatorsToRemove];

    if (sfDisplayType && latestSupportedTypes.includes(sfDisplayType)) {
      operatorsToRemove = [
        ...operatorsToRemove,
        SweepFieldOperator.GREATER_THAN,
        SweepFieldOperator.GREATER_OR_EQUAL,
        SweepFieldOperator.LOWER_THAN,
        SweepFieldOperator.LOWER_OR_EQUAL,
      ];
    }

    const filteredList = operatorList.filter(({ operator }) => {
      return !operatorsToRemove.includes(operator);
    });

    const newOperators = [];

    if (sfDisplayType && minMaxSupportedTypes.includes(sfDisplayType)) {
      newOperators.push(
        { label: 'Is max', operator: SweepFieldOperator.MAX, groupIndex: 0 },
        { label: 'Is min', operator: SweepFieldOperator.MIN, groupIndex: 0 },
      );
    }
    if (sfDisplayType && greatestSupportedTypes.includes(sfDisplayType)) {
      newOperators.push(
        { label: 'Is greatest', operator: SweepFieldOperator.MAX, groupIndex: 0 },
        { label: 'Is lowest', operator: SweepFieldOperator.MIN, groupIndex: 0 },
      );
    }
    if (sfDisplayType && latestSupportedTypes.includes(sfDisplayType)) {
      newOperators.push(
        { label: 'Is latest', operator: SweepFieldOperator.MAX, groupIndex: 0 },
        { label: 'Is earliest', operator: SweepFieldOperator.MIN, groupIndex: 0 },
      );
    }
    if (sfDisplayType && prioritizeSupportedTypes.includes(sfDisplayType)) {
      newOperators.push({
        label: 'Prioritize',
        operator: SweepFieldOperator.PRIORITIZE,
        groupIndex: 0,
      });
    }

    return [...newOperators, ...filteredList];
  };

  return (
    <CreateEditDialog
      open
      onClose={closeOrConfirmClose}
      HeaderProps={{
        hideBackButton: true,
        showCancelButton: true,
        buttonContent: 'Save',
        buttonStartIcon: 'Automations',
        onCancelClick: closeOrConfirmClose,
        onChange: (name: string) => {
          setFormVars({
            ...formVars,
            name: name,
          });
        },
        onButtonClick: () => {
          applyCurrentRule();
        },
        placeholder: 'Rule name',
        value: formVars.name,
        buttonDisabled: !isFormValid || !isAllowedBtn,
        editDisabled: !isAllowedBtn,
        noCreateEditButtons: true,
      }}
      actionsSection={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {hasMultipleCriteriaWithMaxMin && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ErrorIcon sx={{ color: colors.blush[500], fontSize: '20px' }} />
              <Typography variant="body" color={colors.blush[500]}>
                {MULTIPLE_CRITERIA_MAX_MIN_ERROR}
              </Typography>
            </Box>
          )}
          <Box sx={{ marginLeft: 'auto' }}>
            <Button
              size="medium"
              onClick={applyCurrentRule}
              disabled={!isFormValid || !isAllowedBtn}
              startIconName={'Automations'}
            >
              Save
            </Button>
          </Box>
        </Box>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, paddingBottom: 3 }}>
        <SectionBox>
          <Box>
            <Box
              sx={{
                backgroundColor: colors.grey[100],
                paddingTop: '22px',
                borderRadius: '4px',
                '.rbHeader': {
                  padding: 0,
                  margin: 0,
                },
                '.rule-builder-logic-preview': {
                  marginRight: '22px',
                },
              }}
            >
              <CustomHeader customHeader="When these conditions are met" />
              <SweepFieldsRuleBuilder
                filterOperatorBy={filterOperatorBy}
                ref={ruleBuilderRef}
                readOnly={!isAllowedBtn}
                sweepCondition={formVars.when}
                onChange={_onCriteriaChange}
                crmOrgId={crmOrgId}
                objectType={objectType}
                showFieldContextMenu={true}
                disableLookupItemsResolve={true}
                disableResolvePolymorphic
              />
            </Box>
          </Box>
        </SectionBox>
      </Box>
    </CreateEditDialog>
  );
};

const TieBreakerDialogWithProviders = (props: TieBreakerDialogProps) => (
  <MultipleRuleBuildersProvider>
    <TieBreakerDialog {...props} />
  </MultipleRuleBuildersProvider>
);

export { TieBreakerDialogWithProviders as TieBreakerDialog };
